/* Mobile primary navigation: a fixed bottom tab bar (4 core surfaces + More),
   with the remaining surfaces and shell actions in a slide-up sheet. Rendered
   only below 768px. ≥54px targets, safe-area aware, active state via color +
   aria-current (never color alone). */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '../../ds';
import { NAV, NavIcon } from '../nav';
import { useChrome } from '../chrome';
import type { User } from '../../lib/contract';

const PRIMARY = ['command', 'crew', 'site', 'code'];

interface Props {
  user: User;
  crewBadge?: number;
  reviewsBadge?: number;
  onOpenTalk: () => void;
  onSignOut: () => void;
  onStartBuild: () => void;
}

export function MobileNav({ user, crewBadge, reviewsBadge, onOpenTalk, onSignOut, onStartBuild }: Props) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useChrome();
  const [moreOpen, setMoreOpen] = useState(false);

  // Esc closes the More sheet (keyboard parity with the scrim tap).
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const primary = NAV.filter((n) => PRIMARY.includes(n.id));
  const rest = NAV.filter((n) => !PRIMARY.includes(n.id));
  const restActive = rest.some((n) => n.path === pathname);
  const go = (path: string) => {
    setMoreOpen(false);
    nav(path);
  };
  const badgeFor = (id: string) => (id === 'crew' ? crewBadge : id === 'reviews' ? reviewsBadge : undefined);

  const sheetRow = (label: string, icon: React.ReactNode, onClick: () => void, badge?: number) => (
    <button
      key={label}
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 48, padding: '10px 8px', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 15, textAlign: 'left' }}
    >
      <span style={{ display: 'flex', color: 'var(--muted)', flex: 'none' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge ? <span style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'var(--primary-soft)', border: '1px solid var(--primary-line)', color: 'var(--primary)', fontFamily: 'var(--font-mono)', fontSize: 10, display: 'grid', placeItems: 'center' }}>{badge}</span> : null}
    </button>
  );

  return (
    <>
      <nav className="mc-bottombar mc-only-mobile" aria-label="Primary">
        {primary.map((n) => {
          const active = pathname === n.path;
          const badge = badgeFor(n.id);
          return (
            <button key={n.id} className={active ? 'is-active' : ''} aria-current={active ? 'page' : undefined} onClick={() => go(n.path)}>
              <span style={{ position: 'relative', display: 'flex' }}>
                <NavIcon id={n.id} size={20} />
                {badge ? <span className="mc-badge">{badge}</span> : null}
              </span>
              {n.label}
            </button>
          );
        })}
        <button className={restActive || moreOpen ? 'is-active' : ''} aria-expanded={moreOpen} aria-controls="mc-more-sheet" onClick={() => setMoreOpen((v) => !v)}>
          <span style={{ position: 'relative', display: 'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
            {reviewsBadge && restActive === false ? <span className="mc-badge">{reviewsBadge}</span> : null}
          </span>
          More
        </button>
      </nav>

      {moreOpen && (
        <>
          <div className="mc-sheet-scrim" onClick={() => setMoreOpen(false)} aria-hidden="true" />
          <div id="mc-more-sheet" className="mc-sheet" role="dialog" aria-label="More">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 4px 12px' }}>
              <Avatar name={user.name} tone="iris" status="online" size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{user.name}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>{user.role}</div>
              </div>
              <button onClick={() => setMoreOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 6 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </div>

            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', padding: '8px 4px 4px' }}>// surfaces</div>
            {rest.map((n) => sheetRow(n.label, <NavIcon id={n.id} size={19} />, () => go(n.path), badgeFor(n.id)))}

            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', padding: '14px 4px 4px' }}>// actions</div>
            {sheetRow('Talk to AI', <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12Z" /></svg>, () => { setMoreOpen(false); onOpenTalk(); })}
            {sheetRow('New build', <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>, () => { setMoreOpen(false); onStartBuild(); })}
            {sheetRow(theme === 'dark' ? 'Light theme' : 'Dark theme', <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4 12H2M22 12h-2" /></svg>, toggleTheme)}
            {sheetRow('Sign out', <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>, onSignOut)}
          </div>
        </>
      )}
    </>
  );
}
